import { memo, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Types
import { CampaignExecuteResult } from 'types';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useToaster from 'hooks/UseToaster';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import Services
import { CampaignHttpService } from 'services/http';
// Import Components
import DataTable from 'components/common/DataTable';
import AccessControl from 'components/common/AccessControl';
import ConfirmationModal from 'components/common/Modals/ConfirmationModal';
import SectionWithTitle from 'components/common/SectionWithTitle';
import { Button, Tooltip } from '@geneui/components';
// Import Helpers
import { Helpers } from 'services';
// Import Constants
import { l, ModalsClassNames } from 'constants/common';
// Import Configs
import { CampaignActivityTableConfig, CampaignActivityPagingOptions } from './config';
// Import Enums
import { PermissionEnum } from 'enums';

const { recursiveSumObject } = Helpers;

const CampaignActivity = (props) => {
    const {
        isLoadingExecutionResults,
        data,
        count,
        name,
        onDateTimeApply,
        onReloadExecutionResults,
        handleChangePageNumber,
        handleChangePageSize,
        handleChangeSorting,
        segmentData,
    } = props;
    const { t } = useTranslation();
    const { showToaster } = useToaster();
    const { doPostRequest } = useRequest();
    const { partnerDateTimeFormat } = usePartnerDateFormats();

    const [campaignExecuteId, setCampaignExecuteId] = useState();

    const [isRerunModalOpened, setIsRerunModalOpened] = useState(false);
    const [isApplyExecutionDateModalOpened, setIsApplyExecutionDateModalOpened] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const dateRef = useRef<AnyObject>();

    const campaignActivityTableConfig = useMemo(
        () => CampaignActivityTableConfig(t, partnerDateTimeFormat, segmentData),
        [t, partnerDateTimeFormat, segmentData],
    );
    const columns = useMemo(
        () => campaignActivityTableConfig.columns.map((item, index) => ({ ...item, index: index })),
        [campaignActivityTableConfig.columns],
    );

    const { rerunCampaignRequest } = useCancelableRequests({
        rerunCampaignRequest: CampaignHttpService.rerunCampaignExecuteResult,
    });

    const executionData = useMemo(() => {
        return (data || []).map((elem, index) => ({
            index,
            actionBar: false,
            dragDisable: true,
            hasHover: true,
            data: {
                ...elem,
                index,
                CommunicationCount: recursiveSumObject(elem.Stats),
            },
        }));
    }, [data]);

    const rerun = () => {
        setIsLoading(true);
        doPostRequest(rerunCampaignRequest.request, {
            queryString: { campaignExecuteResultId: campaignExecuteId },
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            // Aborted on unmount: no toaster
            if (IsCanceled) return;
            showToaster(AlertType, AlertMessage);
            setIsLoading(false);
            setIsRerunModalOpened(false);
        });
    };

    const rerunCampaignAction = (id) => {
        setCampaignExecuteId(id);
        setIsRerunModalOpened(true);
    };

    const applyDate = () => {
        onDateTimeApply(dateRef.current);
        setIsApplyExecutionDateModalOpened(false);
    };

    const applyDateAction = (startDate, endDate) => {
        dateRef.current = { startDate: startDate, endDate: endDate };
        setIsApplyExecutionDateModalOpened(true);
    };

    const getRowActionBar = (row: { data: CampaignExecuteResult }) => {
        const { data } = row;
        const { CampaignExecuteId, IsRerun, StartDate, EndDate } = data;
        if (data.IsPreCalculate) return null;
        return (
            <div className="crm-flex">
                <Tooltip text={t(l.ApplyExecutionDateAndTime)}>
                    <Button
                        icon="bc-icon-date-filter"
                        appearance="minimal"
                        color="primary"
                        onClick={() => applyDateAction(StartDate, EndDate)}
                    />
                </Tooltip>

                <AccessControl permissions={PermissionEnum.ExecuteCampaign}>
                    <Tooltip text={t(l.ReRunCampaign)}>
                        <Button
                            disabled={!IsRerun}
                            icon="bc-icon-crm-rerun"
                            appearance="minimal"
                            color="primary"
                            onClick={() => rerunCampaignAction(CampaignExecuteId)}
                        />
                    </Tooltip>
                </AccessControl>
            </div>
        );
    };

    return (
        <>
            <SectionWithTitle
                title={t(l.CampaignActivity)}
                titleContent={
                    <Tooltip text={t(l.ReloadData)}>
                        <Button
                            icon="bc-icon-reset"
                            appearance="minimal"
                            color="primary"
                            disabled={isLoadingExecutionResults}
                            onClick={onReloadExecutionResults}
                        />
                    </Tooltip>
                }
                hasContentPadding={false}
                className="c-r-s-with-title"
                contentClassName="c-r-s-with-title-content"
            >
                <DataTable
                    key="CampaignExecuteId"
                    columnKey="dataKey"
                    rowKey="index"
                    name=""
                    data={executionData}
                    dataCount={count}
                    columns={columns}
                    isLoading={isLoadingExecutionResults}
                    withPagination={true}
                    withPageSelector={true}
                    pagingOptions={CampaignActivityPagingOptions}
                    isVisibleOnlyDataTable={false}
                    rowActionBar={getRowActionBar}
                    setPagingPageNumber={handleChangePageNumber}
                    setPagingPageSize={handleChangePageSize}
                    setSorting={handleChangeSorting}
                    sorting={campaignActivityTableConfig.defaultSorting}
                />
            </SectionWithTitle>
            {isRerunModalOpened && (
                <ConfirmationModal
                    onOk={() => rerun()}
                    isVisibleModal={isRerunModalOpened}
                    titleText={t(l.ReRunCampaign)}
                    questionLabel={t(l.DoYouWantToReRunCampaign, { CampaignName: `"${name}"` })}
                    onCancel={() => setIsRerunModalOpened(false)}
                    isLoading={isLoading}
                    iconType="bc-icon-status-play"
                    actionLabel={t(l.ReRun)}
                    className={ModalsClassNames.Start}
                />
            )}
            {isApplyExecutionDateModalOpened && (
                <ConfirmationModal
                    onOk={() => applyDate()}
                    isVisibleModal={isApplyExecutionDateModalOpened}
                    titleText={t(l.ExecutionDate)}
                    questionLabel={t(l.TheDateWillBeChangedByThisExecutionStartAndEndDates)}
                    onCancel={() => setIsApplyExecutionDateModalOpened(false)}
                    iconType="bc-icon-status-play"
                    actionLabel={t(l.Confirm)}
                    className={ModalsClassNames.Start}
                />
            )}
        </>
    );
};

CampaignActivity.propTypes = {
    isLoadingExecutionResults: PropTypes.bool.isRequired,
    campaignId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    data: PropTypes.array,
    count: PropTypes.number,
    onDateTimeApply: PropTypes.func,
    onReloadExecutionResults: PropTypes.func,
    handleChangePageNumber: PropTypes.func,
    handleChangePageSize: PropTypes.func,
    handleChangeSorting: PropTypes.func,
    segmentData: PropTypes.object,
};

CampaignActivity.defaultProps = {
    onDateTimeApply: noop,
    onReloadExecutionResults: noop,
    data: null,
    count: 0,
};

export default memo(CampaignActivity);
